// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import auth0ChangePassword from "@west/App/Handlers/AccountSettings/Auth0ChangePassword";
import { AUTH0_CHANGE_EMAIL_EXTERNAL_REDIRECT } from "@west/App/Services/Auth0Service";
import { getUseExternalLogin } from "@west/App/Store/Selectors/SettingsSelectors";
import { placeHolders } from "@west/XDL/constants";
import { parseBoolean } from "@west/XDL/Utilities";
import parse from "html-react-parser";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    auth0ChangePassword,
    // XNG-Changes
    loadCurrentShipTo,
};

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ changeEmailSuccess?: string }>(location.search);

    return {
        editingAccount: state.pages.accountSettings.editingAccount,
        emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
        useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
        changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
        useExternalLogin: getUseExternalLogin(state),
        pathname: location.pathname,
        showChangeEmailSuccessMessage: parseBoolean(parsedQuery.changeEmailSuccess),
        // XNG-Changes
        currentShipToState: getCurrentShipToState(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    // XNG-Changes
    emailLink?: LinkPresentationProps;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h2",
    },
};

const styles = userInformationStyles;
const password = "*************";

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
    useExternalLogin,
    auth0ChangePassword,
    pathname,
    showChangeEmailSuccessMessage,
    // XNG-Changes
    currentShipToState,
    loadCurrentShipTo,
}: Props) => {
    const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
    const toaster = useToaster();
    const history = useHistory();

    useEffect(() => {
        if (showChangeEmailSuccessMessage) {
            toaster.addToast({
                body: siteMessage("Custom_ChangeEmailSuccessMessage", translate("Email changed successfully")),
                messageType: "success",
            });
            setTimeout(() => {
                history.replace(pathname);
            }, 400);
        }

        if (!currentShipToState.value && !currentShipToState.isLoading) {
            loadCurrentShipTo();
        }
    }, []);

    if (!editingAccount) {
        return null;
    }

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const emailChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (useExternalLogin) {
            return;
        }

        updateAccountSettings({ email: event.currentTarget.value });
    };

    /**
     * Call Auth0 Change password if useExternalLogin enable
     */
    const openAuth0PasswordHandler = () => {
        setChangePasswordModalOpen(true);
    };

    const changeAuth0PasswordHandler = () => {
        auth0ChangePassword({
            onSuccess: result => {
                toaster.addToast({
                    body: parse(result.message || "", parserOptions),
                    messageType: "success",
                });
                setChangePasswordModalOpen(false);
            },
            onError: result => {
                toaster.addToast({ body: parse(result.message || "", parserOptions), messageType: "danger" });
                setChangePasswordModalOpen(false);
            },
        });
    };

    const changePasswordHandler = () => {
        if (changePasswordUrl) {
            history.push(changePasswordUrl);
        }
    };

    /**
     * Call Auth0 Email if useExternalLogin enable
     */
    const emailHandler = () => {
        if (!useExternalLogin) {
            return;
        }

        window.location.href = AUTH0_CHANGE_EMAIL_EXTERNAL_REDIRECT;
    };

    const label = (
        <>
            <Typography>{translate("Password")}</Typography>
            <Link
                {...styles.changePasswordLink}
                onClick={useExternalLogin ? openAuth0PasswordHandler : changePasswordHandler}
                aria-label={translate("Change Password")}
            >
                {translate("Change")}
            </Link>
        </>
    );
    const validation = currentShipToState?.value?.validation;
    const emailLabel = useExternalLogin ? (
        <>
            <Typography>{translate("Email")}</Typography>
            <Link {...styles.emailLink} onClick={emailHandler} aria-label={translate("Email")}>
                {translate("Change")}
            </Link>
        </>
    ) : (
        translate("Email")
    );

    return (
        <>
            <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            <GridContainer>
                {!useEmailAsUserName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField
                            label={translate("User Name")}
                            value={editingAccount.userName}
                            data-test-selector="accountSettings_userName"
                            disabled
                        />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        // XNG-Changes: disable custom validation since it is handled from OOTB
                        type="text"
                        label={emailLabel}
                        onChange={emailChangeHandler}
                        value={editingAccount.email}
                        data-test-selector="accountSettings_email"
                        required
                        maxLength={validation?.email?.maxLength || 256}
                        error={emailErrorMessage}
                        placeholder={placeHolders.email}
                        disabled={useExternalLogin}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField label={translate("Full Name")} value={fullName} disabled />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField type="password" label={label} disabled value={password} />
                </GridItem>
            </GridContainer>
            <TwoButtonModal
                headlineText={siteMessage("Change Password")}
                modalIsOpen={isChangePasswordModalOpen}
                messageText={siteMessage("Custom_Auth0_ChangePassword")}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Send")}
                onCancel={() => setChangePasswordModalOpen(false)}
                onSubmit={() => {
                    changeAuth0PasswordHandler();
                }}
                submitTestSelector="ChangePasswordSubmit"
                dataTestSelector="twoButtonModal_ChangePassword"
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
